//src/pages/CardGame.js
import React, { useState, useEffect, useCallback } from 'react';
import ReactConfetti from 'react-confetti';
import { Helmet } from 'react-helmet';

const Card = ({ id, imageNumber, isFlipped, isMatched, onClick }) => {
  return (
    <div
      className={`aspect-[3/4] cursor-pointer ${isMatched ? 'opacity-70' : ''}`}
      onClick={() => !isFlipped && !isMatched && onClick(id)}
    >
      <img
        src={isFlipped ? `${process.env.PUBLIC_URL}/images/cards/card-${imageNumber}.png` : `${process.env.PUBLIC_URL}/images/cards/back.png`}
        alt={isFlipped ? `Card ${imageNumber}` : 'Card Back'}
        className="w-full h-full object-cover rounded-lg shadow-md"
      />
    </div>
  );
};



const VictoryModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg text-center relative z-60">
        <h3 className="text-2xl font-bold mb-4">Congratulations!</h3>
        <p className="mb-6">You've matched all the cards!</p>
        <button
          onClick={onClose}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Play Again
        </button>
      </div>
    </div>
  );
};

const CardGame = () => {
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [isGameWon, setIsGameWon] = useState(false);
  const [confettiDimensions, setConfettiDimensions] = useState({ width: 0, height: 0 });
  const [numPairs, setNumPairs] = useState(12); // Default to 12 pairs

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const initializeGame = useCallback(() => {
    const initialCards = [];
    for (let i = 1; i <= numPairs; i++) {
      initialCards.push({ id: i * 2 - 1, imageNumber: i, isFlipped: false, isMatched: false });
      initialCards.push({ id: i * 2, imageNumber: i, isFlipped: false, isMatched: false });
    }
    const shuffledCards = shuffleArray(initialCards);
    setCards(shuffledCards);
    setFlippedCards([]);
    setIsGameWon(false);
  }, [numPairs]); // Dependency on numPairs

  useEffect(() => {
    initializeGame();
    // Set confetti dimensions on mount
    setConfettiDimensions({ width: window.innerWidth, height: window.innerHeight });
  }, [initializeGame]); // Now `initializeGame` is included in the dependency array

  const handleCardClick = (id) => {
    if (flippedCards.length === 2) return;
    const updatedCards = cards.map(card =>
      card.id === id ? { ...card, isFlipped: true } : card
    );
    const newFlippedCards = [...flippedCards, id];
    setCards(updatedCards);
    setFlippedCards(newFlippedCards);
    if (newFlippedCards.length === 2) {
      setTimeout(() => checkForMatch(newFlippedCards, updatedCards), 1000);
    }
  };



  const checkForMatch = (newFlippedCards, updatedCards) => {
    const [firstId, secondId] = newFlippedCards;
    const firstCard = updatedCards.find(card => card.id === firstId);
    const secondCard = updatedCards.find(card => card.id === secondId);
    let newCards;
    if (firstCard && secondCard && firstCard.imageNumber === secondCard.imageNumber) {
      newCards = updatedCards.map(card =>
        card.id === firstId || card.id === secondId
          ? { ...card, isMatched: true }
          : card
      );
    } else {
      newCards = updatedCards.map(card =>
        card.id === firstId || card.id === secondId
          ? { ...card, isFlipped: false }
          : card
      );
    }
    setCards(newCards);
    setFlippedCards([]);

    // Check if all cards are matched
    if (newCards.every(card => card.isMatched)) {
      setIsGameWon(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>Card Game | Project V.O.T.E.</title>
      </Helmet>
      <div className="mx-auto max-w-7xl sm:px-4 md:px-6 lg:px-8 h-full">
        <div className="mx-auto max-w-7xl bg-white p-3 rounded-t-lg">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">Memory Game</h1>
            <div className='flex'>
              <button
                onClick={() => setNumPairs(6)}
                className={`mr-2 px-4 py-2 rounded ${numPairs === 6 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
              >
                Easy
              </button>
              <button
                onClick={() => setNumPairs(12)}
                className={`px-4 py-2 rounded ${numPairs === 12 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
              >
                Hard
              </button>
            </div>
          </div>
          <p className="mb-6">Test your memory by matching pairs of voting-related images. Click on the cards to flip them and find matching pairs!</p>
          {cards && cards.length > 0 ? (
            <div className={`grid gap-4 justify-center ${numPairs === 6 ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8'}`}>
              {cards.map(card => (
                <Card
                  key={card.id}
                  {...card}
                  onClick={handleCardClick}
                />
              ))}
            </div>
          ) : (
            <p>Loading cards...</p>
          )}
        </div>
        {isGameWon && (
          <>
            <VictoryModal onClose={initializeGame} />
            <ReactConfetti
              style={{ position: 'fixed', top: 0, left: 0, zIndex: 100 }}
              width={confettiDimensions.width}
              height={confettiDimensions.height}
              recycle={false}
              numberOfPieces={200}
            />
          </>
        )}
      </div>
    </>
  );
}

export default CardGame;
